import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  FUN_WORK_ROWS,
  FUN_WORK_SECTION_TITLE,
} from '../data';
import { STICKER_BOARD_INITIAL } from '../data/stickers';
import Reveal from './Reveal';
import boardStyles from '../styles/StickerBoard.module.css';
import writingStyles from '../styles/Writing.module.css';

type StickerRuntime = {
  id: number;
  src: string;
  top: number;
  left: number;
  z: number;
};

function seedToRuntime(seeds: typeof STICKER_BOARD_INITIAL): StickerRuntime[] {
  return seeds.map((s, i) => ({ ...s, z: i + 1 }));
}

/** Normalized top-left (0–1); keeps entire sticker inside the board. */
function clampStickerInBoard(
  boardRect: DOMRect,
  stickerRect: DOMRect,
  left: number,
  top: number
): { left: number; top: number } {
  if (
    boardRect.width <= 0 ||
    boardRect.height <= 0 ||
    stickerRect.width <= 0 ||
    stickerRect.height <= 0
  ) {
    return { left, top };
  }
  const wRatio = stickerRect.width / boardRect.width;
  const hRatio = stickerRect.height / boardRect.height;
  const maxLeft = Math.max(0, 1 - wRatio);
  const maxTop = Math.max(0, 1 - hRatio);
  return {
    left: Math.max(0, Math.min(maxLeft, left)),
    top: Math.max(0, Math.min(maxTop, top)),
  };
}

const NUDGE_MS = 620;
const NUDGE_INTERVAL_MIN = 3200;
const NUDGE_INTERVAL_JITTER = 1600;

const StickerBoard: React.FC = () => {
  const [stickers, setStickers] = useState<StickerRuntime[]>(() =>
    seedToRuntime(STICKER_BOARD_INITIAL)
  );
  const [dragId, setDragId] = useState<number | null>(null);
  const [nudgeId, setNudgeId] = useState<number | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const dragIdRef = useRef<number | null>(null);
  const visibleRef = useRef(false);
  const nudgeTimeoutRef = useRef<number | null>(null);
  const clearNudgeTimeoutRef = useRef<number | null>(null);
  dragIdRef.current = dragId;

  const clampAllStickers = useCallback(() => {
    const board = boardRef.current;
    if (!board) return;
    const br = board.getBoundingClientRect();
    setStickers((prev) => {
      let changed = false;
      const next = prev.map((s) => {
        const el = document.getElementById(`fun-work-sticker-${s.id}`);
        if (!el) return s;
        const r = el.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) return s;
        const c = clampStickerInBoard(br, r, s.left, s.top);
        if (c.left !== s.left || c.top !== s.top) changed = true;
        return { ...s, ...c };
      });
      return changed ? next : prev;
    });
  }, []);

  useLayoutEffect(() => {
    const board = boardRef.current;
    if (!board) return undefined;
    const run = () => requestAnimationFrame(() => clampAllStickers());
    run();
    const ro = new ResizeObserver(run);
    ro.observe(board);
    return () => ro.disconnect();
  }, [clampAllStickers]);

  useEffect(() => {
    const onWinResize = () => requestAnimationFrame(() => clampAllStickers());
    window.addEventListener('resize', onWinResize);
    return () => window.removeEventListener('resize', onWinResize);
  }, [clampAllStickers]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const board = boardRef.current;
    if (!board) return undefined;

    const stickerIds = STICKER_BOARD_INITIAL.map((s) => s.id);

    const clearChainTimer = () => {
      if (nudgeTimeoutRef.current !== null) {
        window.clearTimeout(nudgeTimeoutRef.current);
        nudgeTimeoutRef.current = null;
      }
    };

    const clearEndClassTimer = () => {
      if (clearNudgeTimeoutRef.current !== null) {
        window.clearTimeout(clearNudgeTimeoutRef.current);
        clearNudgeTimeoutRef.current = null;
      }
    };

    const clearAllNudgeTimers = () => {
      clearChainTimer();
      clearEndClassTimer();
    };

    const runNudge = () => {
      if (!visibleRef.current || dragIdRef.current !== null) return;
      const pick = stickerIds[Math.floor(Math.random() * stickerIds.length)];
      setNudgeId(pick);
      clearEndClassTimer();
      clearNudgeTimeoutRef.current = window.setTimeout(() => {
        clearNudgeTimeoutRef.current = null;
        setNudgeId(null);
      }, NUDGE_MS);
    };

    const scheduleNextNudge = () => {
      clearChainTimer();
      const delay = NUDGE_INTERVAL_MIN + Math.random() * NUDGE_INTERVAL_JITTER;
      nudgeTimeoutRef.current = window.setTimeout(() => {
        nudgeTimeoutRef.current = null;
        if (!visibleRef.current) return;
        runNudge();
        scheduleNextNudge();
      }, delay);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          visibleRef.current = true;
          scheduleNextNudge();
        } else {
          visibleRef.current = false;
          clearAllNudgeTimers();
          setNudgeId(null);
        }
      },
      { threshold: 0.08 }
    );

    io.observe(board);

    return () => {
      visibleRef.current = false;
      clearAllNudgeTimers();
      io.disconnect();
      setNudgeId(null);
    };
  }, []);

  useEffect(() => {
    if (dragId === null) return;
    setNudgeId(null);
  }, [dragId]);

  useEffect(() => {
    if (dragId === null) return undefined;

    const onMove = (ev: PointerEvent) => {
      const board = boardRef.current;
      if (!board) return;
      const br = board.getBoundingClientRect();
      const el = document.getElementById(`fun-work-sticker-${dragId}`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const halfW = r.width / 2;
      const halfH = r.height / 2;
      let left = (ev.clientX - br.left - halfW) / br.width;
      let top = (ev.clientY - br.top - halfH) / br.height;
      const c = clampStickerInBoard(br, r, left, top);
      left = c.left;
      top = c.top;
      setStickers((prev) =>
        prev.map((s) => (s.id === dragId ? { ...s, left, top } : s))
      );
    };

    const end = () => setDragId(null);

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [dragId]);

  const onStickerPointerDown = (id: number) => {
    setDragId(id);
    setStickers((prev) => {
      const maxZ = Math.max(...prev.map((s) => s.z));
      return prev.map((s) => (s.id === id ? { ...s, z: maxZ + 1 } : s));
    });
  };

  return (
    <section
      id="fun-work"
      className={boardStyles.funWork}
      aria-label="Draggable sticker board"
    >
      <div ref={boardRef} className={boardStyles.board}>
        {stickers.map((s) => (
          <img
            key={s.id}
            id={`fun-work-sticker-${s.id}`}
            src={s.src}
            alt=""
            draggable={false}
            className={`${boardStyles.sticker}${
              nudgeId === s.id ? ` ${boardStyles.stickerNudge}` : ''
            }`}
            style={{
              left: `${s.left * 100}%`,
              top: `${s.top * 100}%`,
              zIndex: s.z,
            }}
            onPointerDown={(e) => {
              e.preventDefault();
              onStickerPointerDown(s.id);
            }}
            onLoad={clampAllStickers}
          />
        ))}
      </div>

      <div className={boardStyles.followUp}>
        <Reveal>
          <div className={writingStyles.header}>
            <h2 className={writingStyles.title}>{FUN_WORK_SECTION_TITLE}</h2>
          </div>
        </Reveal>
        <div className={writingStyles.card}>
          {FUN_WORK_ROWS.map((row, i) => {
            const rowStyle = {
              borderBottom:
                i < FUN_WORK_ROWS.length - 1
                  ? '1px solid rgba(0,0,0,0.07)'
                  : 'none',
            };
            const cells = (
              <>
                <span className={writingStyles.postDate}>{row.date}</span>
                <span className={writingStyles.postName}>{row.title}</span>
                <span className={writingStyles.postTime}>{row.time}</span>
              </>
            );
            return (
              <Reveal key={i} delay={i * 0.08}>
                {row.href ? (
                  <a
                    href={row.href}
                    className={writingStyles.postRow}
                    style={rowStyle}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {cells}
                  </a>
                ) : (
                  <div
                    className={`${writingStyles.postRow} ${boardStyles.followUpRowStatic}`}
                    style={rowStyle}
                  >
                    {cells}
                  </div>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StickerBoard;
